import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";
import {
  ConfirmDialog,
  ContentHeader,
  Field,
  Modal,
  MoneyInput,
  QtyInput,
  QueryStatus,
  ScaledInput,
  useToast,
} from "@cx/ui";
import type {
  ListResult,
  PriceListItemInput,
  PriceListItemView,
  PriceListView,
  PriceResolveResult,
} from "@cx/contracts";
import { formatBRL, formatDateBR, formatQty, todayInSaoPaulo, toPct4 } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { PriceListDialog, toValues } from "./PriceListDialog";
import { ProductSearch } from "./ProductSearch";
import {
  BASE_LABEL,
  KIND_LABEL,
  pctNumber,
  priceSourceText,
  rateText,
  ruleText,
  validityText,
} from "./labels";

type ItemTarget = { productId: string; productName: string; initial: PriceListItemInput };

/** Lista de preço: resumo, produtos da Customizada e simulação do preço efetivo (05 §2). */
export function PriceListPage(): ReactElement {
  const { id } = useParams({ from: "/cadastros/listas-de-precos/$id" });
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const perms = me.permissions as Permissions;
  const key = ["price-lists", companyId, id];
  const list = useQuery({ queryKey: key, queryFn: () => api<PriceListView>(`/price-lists/${id}`) });
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const del = useMutation({
    mutationFn: () => api(`/price-lists/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("success", "Lista de preço excluída");
      qc.invalidateQueries({ queryKey: ["price-lists", companyId] });
      qc.invalidateQueries({ queryKey: ["deletions", companyId] });
      navigate({ to: "/cadastros/listas-de-precos" });
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const l = list.data;
  // Custo ± % revelaria o custo: a API recusa a simulação sobre o custo para quem não tem products:read_cost.
  const costHidden = l?.base === "cost" && !can(perms, "products", "read_cost");
  return (
    <>
      <ContentHeader
        title={l?.name ?? "Lista de preço"}
        breadcrumb={["Cadastros", "Listas de preços", l?.name ?? ""]}
        actions={
          l && (
            <>
              <Link to="/cadastros/listas-de-precos" className="btn btn-outline-secondary">
                Voltar
              </Link>
              {can(perms, "price_lists", "update") && (
                <button type="button" className="btn btn-primary" onClick={() => setEditing(true)}>
                  Editar lista
                </button>
              )}
              {can(perms, "price_lists", "delete") && (
                <button type="button" className="btn btn-outline-danger" onClick={() => setDeleting(true)}>
                  Excluir lista
                </button>
              )}
            </>
          )
        }
      />
      <QueryStatus loading={list.isLoading} error={list.error} />
      {l && (
        <>
          <div className="box card p-3 mb-3">
            <dl className="row mb-0" aria-label="Resumo da lista">
              <dt className="col-sm-3">Tipo</dt>
              <dd className="col-sm-9">{KIND_LABEL[l.kind]}</dd>
              <dt className="col-sm-3">Regra</dt>
              <dd className="col-sm-9" data-testid="price-list-rule">
                {ruleText(l)}
              </dd>
              <dt className="col-sm-3">Vigência</dt>
              <dd className="col-sm-9">{validityText(l.validFrom, l.validTo)}</dd>
              <dt className="col-sm-3">Situação</dt>
              <dd className="col-sm-9 mb-0">{l.active ? "Ativa" : "Inativa"}</dd>
            </dl>
          </div>
          {l.kind === "custom" && <ItemsBox list={l} />}
          {!costHidden && <SimulateBox listId={l.id} />}
        </>
      )}
      {editing && l && (
        <PriceListDialog
          id={l.id}
          initial={toValues(l)}
          onClose={() => setEditing(false)}
          onSaved={() => qc.invalidateQueries({ queryKey: ["price-lists", companyId] })}
        />
      )}
      <ConfirmDialog
        open={deleting}
        title="Excluir lista de preço"
        message={`Excluir a lista "${l?.name ?? ""}"? Ela pode ser restaurada pela Consulta de exclusões.`}
        confirmLabel="Excluir"
        onConfirm={() => {
          del.mutate();
          setDeleting(false);
        }}
        onCancel={() => setDeleting(false)}
      />
    </>
  );
}

function ItemsBox({ list }: { list: PriceListView }) {
  const { companyId, me, api } = useSession();
  const qc = useQueryClient();
  const toast = useToast();
  const perms = me.permissions as Permissions;
  const canEdit = can(perms, "price_lists", "update");
  // Na lista de custo, sem products:read_cost o produto só leva preço fixo (o % sobre o custo fica oculto).
  const rateAllowed = list.base !== "cost" || can(perms, "products", "read_cost");
  const [q, setQ] = useState("");
  const term = q.trim();
  const key = ["price-lists", companyId, list.id, "items", term];
  const items = useInfiniteQuery({
    queryKey: key,
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<ListResult<PriceListItemView>>(
        `/price-lists/${list.id}/items?${new URLSearchParams({
          ...(term ? { q: term } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
          limit: "50",
        })}`,
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = items.data?.pages.flatMap((p) => p.data) ?? [];
  const [target, setTarget] = useState<ItemTarget | null>(null);
  const changed = () => qc.invalidateQueries({ queryKey: ["price-lists", companyId] });
  const remove = useMutation({
    mutationFn: (productId: string) =>
      api(`/price-lists/${list.id}/items/${productId}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("success", "Produto removido da lista");
      changed();
    },
    onError: (e) => toast("danger", errorMessage(e)),
  });
  const priceText = (i: PriceListItemView) =>
    i.priceCents !== null ? formatBRL(i.priceCents) : rateText(i.ratePct4, list.base);
  return (
    <div className="box card p-3 mb-3">
      <h2 className="h6">Produtos da lista</h2>
      {canEdit && (
        <ProductSearch
          id="price-item-search"
          label="Adicionar produto"
          action="Adicionar"
          onPick={(p) =>
            setTarget({
              productId: p.id,
              productName: p.name,
              initial: { priceCents: p.priceCents, ratePct4: null, minQtyQ4: null },
            })
          }
        />
      )}
      <div className="mb-2">
        <label className="form-label visually-hidden" htmlFor="price-item-filter">
          Filtrar produtos da lista
        </label>
        <input
          id="price-item-filter"
          type="search"
          className="form-control"
          placeholder="Filtrar por nome ou SKU"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      <QueryStatus loading={items.isLoading} error={items.error} />
      {items.isSuccess && rows.length === 0 && (
        <p className="text-muted mb-0">Nenhum produto na lista. Sem item, vale o preço do cadastro.</p>
      )}
      {rows.length > 0 && (
        <div className="table-responsive">
          <table className="table align-middle mb-0" aria-label="Produtos da lista">
            <thead>
              <tr>
                <th scope="col">Produto</th>
                <th scope="col" className="text-end">
                  Preço do cadastro
                </th>
                <th scope="col" className="text-end">
                  Preço na lista
                </th>
                <th scope="col" className="text-end">
                  Qtd. mínima
                </th>
                <th scope="col">
                  <span className="visually-hidden">Ações</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((i) => (
                <tr key={i.productId}>
                  <td>
                    {i.productName}
                    {i.sku && <span className="text-muted small ms-2">{i.sku}</span>}
                  </td>
                  <td className="text-end">{formatBRL(i.productPriceCents)}</td>
                  <td className="text-end">{priceText(i)}</td>
                  <td className="text-end">
                    {i.minQtyQ4 === null ? "—" : `${formatQty(i.minQtyQ4)} ${i.unit}`}
                  </td>
                  <td className="text-end text-nowrap">
                    {canEdit && (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary me-2"
                          aria-label={`Editar preço de ${i.productName}`}
                          onClick={() =>
                            setTarget({
                              productId: i.productId,
                              productName: i.productName,
                              initial: {
                                priceCents: i.priceCents,
                                ratePct4: i.ratePct4 ?? null,
                                minQtyQ4: i.minQtyQ4,
                              },
                            })
                          }
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          aria-label={`Remover ${i.productName}`}
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(i.productId)}
                        >
                          Remover
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {items.hasNextPage && (
        <button
          type="button"
          className="btn btn-outline-secondary mt-2"
          disabled={items.isFetchingNextPage}
          onClick={() => items.fetchNextPage()}
        >
          Carregar mais
        </button>
      )}
      {target && (
        <ItemDialog
          listId={list.id}
          base={list.base}
          rateAllowed={rateAllowed}
          target={target}
          onClose={() => setTarget(null)}
          onSaved={changed}
        />
      )}
    </div>
  );
}

function ItemDialog({
  listId,
  base,
  rateAllowed,
  target,
  onClose,
  onSaved,
}: {
  listId: string;
  base: PriceListView["base"];
  rateAllowed: boolean;
  target: ItemTarget;
  onClose(): void;
  onSaved(): void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const [open, setOpen] = useState(true);
  const [mode, setMode] = useState<"price" | "rate">(
    rateAllowed && target.initial.ratePct4 !== null ? "rate" : "price",
  );
  const [v, setV] = useState(target.initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: () =>
      api(`/price-lists/${listId}/items/${target.productId}`, {
        method: "PUT",
        body: JSON.stringify({
          priceCents: mode === "price" ? v.priceCents : null,
          ratePct4: mode === "rate" ? v.ratePct4 : null,
          minQtyQ4: v.minQtyQ4,
        }),
      }),
    onSuccess: () => {
      toast("success", "Preço do produto salvo");
      onSaved();
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      toast("danger", errorMessage(e));
    },
  });
  return (
    <Modal
      open={open}
      title={`Preço de ${target.productName}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <fieldset className="mb-3">
        <legend className="form-label fs-6">Regra</legend>
        {(
          [
            ["price", "Preço fixo"],
            ["rate", `Percentual sobre o ${BASE_LABEL[base]}`],
          ] as const
        )
          .filter(([m]) => m === "price" || rateAllowed)
          .map(([m, label]) => (
            <div key={m} className="form-check form-check-inline">
              <input
                id={`item-mode-${m}`}
                type="radio"
                name="item-mode"
                className="form-check-input"
                checked={mode === m}
                onChange={() => setMode(m)}
              />
              <label className="form-check-label" htmlFor={`item-mode-${m}`}>
                {label}
              </label>
            </div>
          ))}
      </fieldset>
      <div className="row">
        {mode === "price" ? (
          <Field label="Preço na lista" required className="col-md-6" error={errors.priceCents}>
            <MoneyInput value={v.priceCents} onChange={(c) => setV({ ...v, priceCents: c })} />
          </Field>
        ) : (
          <Field
            label="Percentual"
            required
            className="col-md-6"
            error={errors.ratePct4}
            hint="Negativo = desconto (ex.: -10)."
          >
            <ScaledInput
              value={v.ratePct4}
              parse={toPct4}
              format={pctNumber}
              suffix="%"
              onChange={(p) => setV({ ...v, ratePct4: p })}
            />
          </Field>
        )}
        <Field
          label="Quantidade mínima"
          className="col-md-6"
          error={errors.minQtyQ4}
          hint="Abaixo dela vale o preço do cadastro."
        >
          <QtyInput value={v.minQtyQ4} decimals={0} onChange={(q) => setV({ ...v, minQtyQ4: q })} />
        </Field>
      </div>
    </Modal>
  );
}

/** Simula o preço efetivo de um produto na lista (GET /price-lists/:id/resolve). */
function SimulateBox({ listId }: { listId: string }) {
  const { companyId, api } = useSession();
  const [product, setProduct] = useState<{ id: string; name: string } | null>(null);
  const [qty, setQty] = useState<number | null>(10_000);
  const [date, setDate] = useState(todayInSaoPaulo());
  const ready = !!product && qty !== null && /^\d{4}-\d{2}-\d{2}$/.test(date);
  const r = useQuery({
    queryKey: ["price-lists", companyId, listId, "resolve", product?.id, qty, date],
    enabled: ready,
    queryFn: () =>
      api<PriceResolveResult>(
        `/price-lists/${listId}/resolve?${new URLSearchParams({
          productId: product!.id,
          qtyQ4: String(qty),
          date,
        })}`,
      ),
  });
  return (
    <div className="box card p-3">
      <h2 className="h6">Simular preço</h2>
      <ProductSearch
        id="simulate-product-search"
        label="Produto"
        action="Simular"
        onPick={(p) => setProduct({ id: p.id, name: p.name })}
      />
      <div className="row">
        <Field label="Quantidade" className="col-md-4">
          <QtyInput value={qty} decimals={0} onChange={setQty} />
        </Field>
        <Field label="Data" className="col-md-4">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
      </div>
      {product && (
        <div aria-live="polite" data-testid="simulated-price">
          <QueryStatus loading={r.isLoading} error={r.error} />
          {r.data && (
            <p className="mb-0">
              <strong>{product.name}</strong>: {formatBRL(r.data.priceCents)}{" "}
              <span className="text-muted">
                ({priceSourceText(r.data)}; cadastro {formatBRL(r.data.basePriceCents)}; em{" "}
                {formatDateBR(r.data.date)})
              </span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
